<template>
  <div class="min-vh-100 d-flex flex-column bg-page">
    <!-- Top nav -->
    <nav
      class="navbar navbar-expand-lg navbar-light bg-white border-bottom sticky-top nav-blur"
    >
      <div class="container">
        <router-link
          to="/"
          class="navbar-brand d-flex align-items-center gap-2"
        >
          <hr-payroll-mark variant="full" />
        </router-link>

        <button
          class="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#publicNavbar"
        >
          <span class="navbar-toggler-icon"></span>
        </button>

        <div class="collapse navbar-collapse" id="publicNavbar">
          <ul class="navbar-nav ms-auto mb-2 mb-lg-0 align-items-lg-center">
            <li class="nav-item me-lg-3">
              <a class="nav-link fw-semibold" href="#features">Features</a>
            </li>
            <li class="nav-item me-lg-3">
              <a class="nav-link fw-semibold" href="#deep-dive">Modules</a>
            </li>
            <li class="nav-item me-lg-3">
              <a class="nav-link fw-semibold" href="#how-it-works"
                >How it works</a
              >
            </li>
            <li class="nav-item me-lg-3">
              <a class="nav-link fw-semibold" href="#pricing">Pricing</a>
            </li>
            <li class="nav-item me-lg-3">
              <a class="nav-link fw-semibold" href="#faq">FAQ</a>
            </li>
            <li class="nav-item me-lg-3">
              <button
                class="nav-link fw-semibold btn btn-link p-0"
                type="button"
                @click="openDemoModal"
              >
                Request Demo
              </button>
            </li>
          </ul>

          <div class="d-flex gap-2 ms-lg-3">
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm px-3"
              @click="openWorkspaceSignIn"
            >
              Sign In
            </button>
            <router-link to="/register" class="btn btn-primary btn-sm px-3">
              Start Free Trial
            </router-link>
          </div>
        </div>
      </div>
    </nav>

    <!-- Page content -->
    <main class="flex-grow-1">
      <!-- HERO -->
      <section class="hero-wrap py-5 py-lg-6">
        <div class="container position-relative">
          <div class="row align-items-center g-4">
            <div class="col-lg-6">
              <div
                class="d-inline-flex align-items-center gap-2 badge-pill mb-3"
              >
                <i class="ti ti-sparkles"></i>
                <span class="fw-semibold"
                  >All-in-one business operating system</span
                >
                <span class="dot"></span>
                <span class="text-muted">Built for Africa</span>
              </div>

              <h1 class="display-5 fw-bold mb-3">
                One platform to run
                <span class="text-primary">HR, Payroll</span>, Attendance, CRM,
                Projects and Accounting.
              </h1>

              <p class="lead text-muted mb-4">
                Replace disconnected spreadsheets and tools with a single,
                modern system designed for fast-growing teams. Get approvals,
                compliance-ready payroll, and operational visibility — in one
                place.
              </p>

              <div
                class="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 mb-4"
              >
                <button
                  type="button"
                  class="btn btn-outline-secondary btn-lg px-4"
                  @click="openDemoModal"
                >
                  Request Demo <i class="ti ti-calendar-event ms-2"></i>
                </button>

                <router-link to="/register" class="btn btn-primary btn-lg px-4">
                  Start Free Trial <i class="ti ti-arrow-right ms-2"></i>
                </router-link>
                <a
                  href="#pricing"
                  class="btn btn-outline-secondary btn-lg px-4"
                >
                  View Pricing
                </a>
              </div>

              <div class="d-flex flex-wrap gap-3 text-muted small">
                <span class="d-inline-flex align-items-center gap-2">
                  <i class="ti ti-shield-check text-success"></i>
                  No credit card required
                </span>
                <span class="d-inline-flex align-items-center gap-2">
                  <i class="ti ti-clock-check text-success"></i>
                  Cancel anytime
                </span>
                <span class="d-inline-flex align-items-center gap-2">
                  <i class="ti ti-file-certificate text-success"></i>
                  Compliance-ready payroll
                </span>
              </div>

              <!-- Social proof -->
              <div class="mt-4">
                <div class="text-muted small fw-semibold mb-2">
                  Trusted by growing teams
                </div>
                <div class="d-flex flex-wrap gap-2">
                  <span class="logo-pill"
                    ><i class="ti ti-building"></i> SMEs</span
                  >
                  <span class="logo-pill"
                    ><i class="ti ti-truck"></i> Logistics</span
                  >
                  <span class="logo-pill"
                    ><i class="ti ti-hospital"></i> Clinics</span
                  >
                  <span class="logo-pill"
                    ><i class="ti ti-device-laptop"></i> Tech</span
                  >
                  <span class="logo-pill"
                    ><i class="ti ti-building-bank"></i> Agencies</span
                  >
                </div>
              </div>
            </div>

            <div class="col-lg-6">
              <!-- Product Preview with tabs + screenshot slot -->
              <div class="product-shell shadow-sm border-0">
                <div
                  class="mockup-top d-flex align-items-center justify-content-between"
                >
                  <div class="d-flex align-items-center gap-2">
                    <span class="dot-red"></span>
                    <span class="dot-yellow"></span>
                    <span class="dot-green"></span>
                  </div>
                  <span class="text-muted small fw-semibold"
                    >Features Preview</span
                  >
                </div>

                <div class="p-3 p-lg-4">
                  <div ref="tabsWrapRef" class="tabs-wrap mb-3">
                    <button
                      v-for="t in FEATURE_TABS"
                      :key="t.key"
                      class="tab-btn"
                      :class="{ active: activeTab === t.key }"
                      type="button"
                      @click="activeTab = t.key"
                    >
                      <i :class="t.icon"></i>
                      <span class="d-none d-sm-inline">{{ t.label }}</span>
                    </button>
                  </div>

                  <div class="screen-wrap mb-3">
                    <div class="screen-inner">
                      <div class="screen-kpis">
                        <div class="screen-kpi">
                          <div class="text-muted small">Employees</div>
                          <div class="fw-bold">128</div>
                        </div>
                        <div class="screen-kpi">
                          <div class="text-muted small">Attendance</div>
                          <div class="fw-bold">97%</div>
                        </div>
                        <div class="screen-kpi">
                          <div class="text-muted small">Payroll</div>
                          <div class="fw-bold">Ready</div>
                        </div>
                        <div class="screen-kpi">
                          <div class="text-muted small">Open Deals</div>
                          <div class="fw-bold">24</div>
                        </div>
                      </div>

                      <div class="screen-grid">
                        <div class="screen-card">
                          <div class="fw-semibold mb-1">
                            {{ activeTabMeta.title }}
                          </div>
                          <div class="text-muted small">
                            {{ activeTabMeta.subtitle }}
                          </div>

                          <div class="mt-3 d-flex flex-wrap gap-2">
                            <span
                              v-for="chip in activeTabMeta.chips"
                              :key="chip"
                              class="chip-pill"
                            >
                              <i class="ti ti-check"></i> {{ chip }}
                            </span>
                          </div>
                        </div>

                        <div class="screen-card screen-card--lite">
                          <div
                            class="d-flex align-items-center justify-content-between mb-2"
                          >
                            <div class="fw-semibold">Today</div>
                            <span class="badge bg-light text-dark border"
                              >Live</span
                            >
                          </div>

                          <div class="mini-list">
                            <div class="mini-row">
                              <i class="ti ti-circle-check text-success"></i>
                              <span>Approvals up to date</span>
                            </div>
                            <div class="mini-row">
                              <i class="ti ti-clock text-primary"></i>
                              <span>Schedules synced</span>
                            </div>
                            <div class="mini-row">
                              <i class="ti ti-receipt-tax text-primary"></i>
                              <span>Payslips ready</span>
                            </div>
                            <div class="mini-row">
                              <i
                                class="ti ti-report-analytics text-primary"
                              ></i>
                              <span>Reports generated</span>
                            </div>
                          </div>

                          <!-- <div class="mt-3 text-muted small">
                            Tip: swap this “preview” later with a real
                            screenshot using
                            <code>&lt;img&gt;</code>.
                          </div> -->
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="text-center text-muted small">
                    <i class="ti ti-lock me-1"></i>
                    Secure by design · Role-based access · Company-scoped data
                  </div>
                </div>
              </div>
              <div class="text-center text-muted small mt-2 d-lg-none">
                <i class="ti ti-swipe me-1"></i>
                Tap tabs to preview modules
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- VALUE STATS -->
      <section class="py-4">
        <div class="container">
          <div class="stats-card">
            <div class="row g-3 align-items-center">
              <div class="col-md-4">
                <div class="d-flex align-items-center gap-3">
                  <span class="stat-icon"
                    ><i class="ti ti-trending-up"></i
                  ></span>
                  <div>
                    <div class="fw-bold">Reduce payroll processing time</div>
                    <div class="text-muted small">
                      Automate calculations, approvals and exports.
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="d-flex align-items-center gap-3">
                  <span class="stat-icon"><i class="ti ti-refresh"></i></span>
                  <div>
                    <div class="fw-bold">Fewer tools, no errors</div>
                    <div class="text-muted small">
                      HR, attendance, CRM and finance stay connected.
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="d-flex align-items-center gap-3">
                  <span class="stat-icon"
                    ><i class="ti ti-report-analytics"></i
                  ></span>
                  <div>
                    <div class="fw-bold">Real-time reporting</div>
                    <div class="text-muted small">
                      Dashboards for HR, Finance and leadership.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- FEATURES -->
      <section id="features" class="py-5">
        <div class="container">
          <div class="text-center mb-4">
            <span
              class="badge bg-soft-primary text-primary fw-semibold px-3 py-2 rounded-pill mb-2"
            >
              <i class="ti ti-stars me-1"></i> Core Modules
            </span>
            <h2 class="fw-bold mb-2">
              Everything connected — built for speed and clarity
            </h2>
            <p class="text-muted mb-0">
              From onboarding to payroll and performance, then CRM to invoicing
              and projects — each module talks to the other.
            </p>
          </div>

          <div class="row g-4">
            <div class="col-md-4">
              <div class="feature-card h-100">
                <div class="d-flex align-items-center gap-3 mb-2">
                  <span class="feature-icon"><i class="ti ti-users"></i></span>
                  <h5 class="mb-0">HR & People</h5>
                </div>
                <p class="text-muted mb-3 small">
                  Employee profiles, departments, onboarding, documents and
                  approvals.
                </p>
                <ul class="list-unstyled small mb-0">
                  <li class="d-flex gap-2 mb-2">
                    <i class="ti ti-check text-success"></i> Central employee
                    record
                  </li>
                  <li class="d-flex gap-2 mb-2">
                    <i class="ti ti-check text-success"></i> Role-based access
                  </li>
                  <li class="d-flex gap-2">
                    <i class="ti ti-check text-success"></i> HR reports &
                    exports
                  </li>
                </ul>
              </div>
            </div>

            <div class="col-md-4">
              <div class="feature-card h-100">
                <div class="d-flex align-items-center gap-3 mb-2">
                  <span class="feature-icon"><i class="ti ti-clock"></i></span>
                  <h5 class="mb-0">Attendance & Leave</h5>
                </div>
                <p class="text-muted mb-3 small">
                  Shifts, schedules, punch in/out, approvals, leave balances and
                  summaries.
                </p>
                <ul class="list-unstyled small mb-0">
                  <li class="d-flex gap-2 mb-2">
                    <i class="ti ti-check text-success"></i> Shift-based
                    attendance
                  </li>
                  <li class="d-flex gap-2 mb-2">
                    <i class="ti ti-check text-success"></i> Approvals & audits
                  </li>
                  <li class="d-flex gap-2">
                    <i class="ti ti-check text-success"></i> Timesheets ready
                  </li>
                </ul>
              </div>
            </div>

            <div class="col-md-4">
              <div class="feature-card h-100">
                <div class="d-flex align-items-center gap-3 mb-2">
                  <span class="feature-icon"
                    ><i class="ti ti-receipt-tax"></i
                  ></span>
                  <h5 class="mb-0">Payroll & Compliance</h5>
                </div>
                <p class="text-muted mb-3 small">
                  Fast payroll runs with localized rules, payslips, P9, and
                  statutory deductions support.
                </p>
                <ul class="list-unstyled small mb-0">
                  <li class="d-flex gap-2 mb-2">
                    <i class="ti ti-check text-success"></i> PAYE / SHA / NSSF
                    flows
                  </li>
                  <li class="d-flex gap-2 mb-2">
                    <i class="ti ti-check text-success"></i> Payslips & exports
                  </li>
                  <li class="d-flex gap-2">
                    <i class="ti ti-check text-success"></i> Approval workflow
                  </li>
                </ul>
              </div>
            </div>

            <div class="col-md-4">
              <div class="feature-card h-100">
                <div class="d-flex align-items-center gap-3 mb-2">
                  <span class="feature-icon"
                    ><i class="ti ti-briefcase"></i
                  ></span>
                  <h5 class="mb-0">CRM</h5>
                </div>
                <p class="text-muted mb-3 small">
                  Leads, deals, pipelines, activities and analytics — connected
                  to invoices and projects.
                </p>
                <ul class="list-unstyled small mb-0">
                  <li class="d-flex gap-2 mb-2">
                    <i class="ti ti-check text-success"></i> Pipeline stages
                  </li>
                  <li class="d-flex gap-2 mb-2">
                    <i class="ti ti-check text-success"></i> Deal tracking
                  </li>
                  <li class="d-flex gap-2">
                    <i class="ti ti-check text-success"></i> Reports &
                    dashboards
                  </li>
                </ul>
              </div>
            </div>

            <div class="col-md-4">
              <div class="feature-card h-100">
                <div class="d-flex align-items-center gap-3 mb-2">
                  <span class="feature-icon"
                    ><i class="ti ti-layout-kanban"></i
                  ></span>
                  <h5 class="mb-0">Projects & Tasks</h5>
                </div>
                <p class="text-muted mb-3 small">
                  Kanban boards, tasks, assignments, updates and delivery
                  tracking across teams.
                </p>
                <ul class="list-unstyled small mb-0">
                  <li class="d-flex gap-2 mb-2">
                    <i class="ti ti-check text-success"></i> Kanban workflow
                  </li>
                  <li class="d-flex gap-2 mb-2">
                    <i class="ti ti-check text-success"></i> Timesheets
                  </li>
                  <li class="d-flex gap-2">
                    <i class="ti ti-check text-success"></i> Delivery reporting
                  </li>
                </ul>
              </div>
            </div>

            <div class="col-md-4">
              <div class="feature-card h-100">
                <div class="d-flex align-items-center gap-3 mb-2">
                  <span class="feature-icon"
                    ><i class="ti ti-calculator"></i
                  ></span>
                  <h5 class="mb-0">Finance & Accounting</h5>
                </div>
                <p class="text-muted mb-3 small">
                  Invoices, estimates, expenses, accounting views and connected
                  reporting.
                </p>
                <ul class="list-unstyled small mb-0">
                  <li class="d-flex gap-2 mb-2">
                    <i class="ti ti-check text-success"></i> Invoicing
                  </li>
                  <li class="d-flex gap-2 mb-2">
                    <i class="ti ti-check text-success"></i> Expenses
                  </li>
                  <li class="d-flex gap-2">
                    <i class="ti ti-check text-success"></i> Finance reporting
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Security / Multi-tenant strip -->
          <div class="mt-5">
            <div class="strip-card">
              <div class="row g-3 align-items-center">
                <div class="col-md-8">
                  <div class="d-flex align-items-start gap-3">
                    <span class="strip-icon"><i class="ti ti-lock"></i></span>
                    <div>
                      <div class="fw-bold">
                        Secure, company-scoped data and role-based access
                      </div>
                      <div class="text-muted small">
                        Built for multi-company environments: access control,
                        approvals, and auditability.
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-4 text-md-end">
                  <router-link to="/register" class="btn btn-primary px-4">
                    Try it now <i class="ti ti-arrow-right ms-2"></i>
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- MODULE DEEP DIVE -->
      <section id="deep-dive" class="py-5 bg-white">
        <div class="container">
          <div class="text-center mb-4">
            <span
              class="badge bg-soft-primary text-primary fw-semibold px-3 py-2 rounded-pill mb-2"
            >
              <i class="ti ti-layers-subtract me-1"></i> Module Deep Dive
            </span>
            <h2 class="fw-bold mb-2">Explore modules in detail</h2>
            <p class="text-muted mb-0">
              One platform, multiple departments — all connected by approvals,
              reporting and audit trails.
            </p>
          </div>

          <div class="row g-4 align-items-start">
            <!-- Sticky left menu -->
            <div class="col-lg-4">
              <div class="deep-menu sticky-lg-top">
                <div class="deep-menu-head">
                  <div class="fw-bold">Modules</div>
                  <div class="text-muted small">
                    Pick a module to see highlights
                  </div>
                </div>

                <button
                  v-for="m in DEEP_DIVE"
                  :key="m.key"
                  type="button"
                  class="deep-item"
                  :class="{ active: deepActive === m.key }"
                  @click="deepActive = m.key"
                >
                  <span class="deep-ico">
                    <i :class="m.icon"></i>
                  </span>
                  <span class="flex-grow-1 text-start">
                    <span class="d-block fw-semibold">{{ m.label }}</span>
                    <span class="d-block small text-muted">{{
                      m.tagline
                    }}</span>
                  </span>
                  <i class="ti ti-chevron-right text-muted"></i>
                </button>

                <div class="deep-cta mt-3">
                  <div class="fw-bold">Want a guided demo?</div>
                  <div class="text-muted small mb-3">
                    Start your trial, then explore modules with your team.
                  </div>
                  <router-link to="/register" class="btn btn-primary w-100">
                    Start Free Trial <i class="ti ti-arrow-right ms-2"></i>
                  </router-link>
                </div>
              </div>
            </div>

            <!-- Right content panel -->
            <div class="col-lg-8">
              <div class="deep-panel shadow-sm border-0">
                <div class="deep-panel-top">
                  <div class="d-flex align-items-start gap-3">
                    <span class="deep-panel-ico">
                      <i :class="deepMeta.icon"></i>
                    </span>
                    <div class="flex-grow-1">
                      <div class="d-flex flex-wrap gap-2 align-items-center">
                        <h3 class="fw-bold mb-0">{{ deepMeta.title }}</h3>
                        <span
                          v-if="deepMeta.badge"
                          class="badge bg-primary rounded-pill"
                        >
                          {{ deepMeta.badge }}
                        </span>
                      </div>
                      <div class="text-muted mt-1">
                        {{ deepMeta.description }}
                      </div>
                    </div>
                  </div>
                </div>

                <div class="p-4">
                  <div class="row g-3">
                    <div class="col-md-6">
                      <div class="deep-card">
                        <div class="fw-semibold mb-2">
                          <i class="ti ti-bolt text-primary me-2"></i> Key
                          capabilities
                        </div>
                        <ul class="list-unstyled small mb-0">
                          <li
                            v-for="x in deepMeta.capabilities"
                            :key="x"
                            class="d-flex gap-2 mb-2"
                          >
                            <i class="ti ti-check text-success"></i>
                            <span>{{ x }}</span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div class="col-md-6">
                      <div class="deep-card">
                        <div class="fw-semibold mb-2">
                          <i class="ti ti-chart-bar text-primary me-2"></i>
                          Reports you get
                        </div>
                        <ul class="list-unstyled small mb-0">
                          <li
                            v-for="r in deepMeta.reports"
                            :key="r"
                            class="d-flex gap-2 mb-2"
                          >
                            <i class="ti ti-point-filled text-primary"></i>
                            <span>{{ r }}</span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div class="col-12">
                      <div class="deep-strip">
                        <div
                          class="d-flex flex-wrap gap-2 align-items-center justify-content-between"
                        >
                          <div class="d-flex align-items-center gap-2">
                            <i class="ti ti-link text-primary"></i>
                            <span class="fw-semibold">Connected modules</span>
                            <span class="text-muted small"
                              >— data flows across the platform</span
                            >
                          </div>
                          <div class="d-flex flex-wrap gap-2">
                            <span
                              v-for="c in deepMeta.connected"
                              :key="c"
                              class="badge bg-light text-dark border"
                            >
                              {{ c }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- mini “preview grid” -->
                    <div class="col-12">
                      <div class="deep-preview">
                        <div class="deep-preview-head">
                          <div class="fw-semibold">Preview</div>
                          <div class="text-muted small">
                            Sample screens & workflow hints
                          </div>
                        </div>
                        <div class="deep-preview-grid">
                          <div
                            class="preview-tile"
                            v-for="p in deepMeta.preview"
                            :key="p.title"
                          >
                            <div
                              class="d-flex align-items-center justify-content-between"
                            >
                              <div class="fw-semibold">{{ p.title }}</div>
                              <!-- <span
                                class="badge bg-soft-primary text-primary border-0"
                                >Screen</span
                              > -->
                            </div>
                            <div class="text-muted small mt-1">
                              {{ p.note }}
                            </div>
                            <div class="mt-3 d-flex flex-wrap gap-2">
                              <span
                                v-for="chip in p.chips"
                                fReady
                                :key="chip"
                                class="chip-pill chip-pill--sm"
                              >
                                <i class="ti ti-check"></i> {{ chip }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- CTA -->
                    <div class="col-12">
                      <div class="cta-card">
                        <div class="row g-3 align-items-center">
                          <div class="col-md-8">
                            <div class="fw-bold">
                              Ready to try {{ deepMeta.title }}?
                            </div>
                            <div class="text-muted">
                              Start your free trial and explore the full
                              workflow end-to-end.
                            </div>
                          </div>
                          <div class="col-md-4 text-md-end">
                            <router-link
                              to="/register"
                              class="btn btn-primary btn-lg px-4"
                            >
                              Start Free Trial
                              <i class="ti ti-arrow-right ms-2"></i>
                            </router-link>
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- /CTA -->
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- HOW IT WORKS -->
      <section id="how-it-works" class="py-5 bg-white">
        <div class="container">
          <div class="text-center mb-4">
            <span
              class="badge bg-soft-primary text-primary fw-semibold px-3 py-2 rounded-pill mb-2"
            >
              <i class="ti ti-route me-1"></i> Getting Started
            </span>
            <h2 class="fw-bold mb-2">Go live in 3 simple steps</h2>
            <p class="text-muted mb-0">
              No long setup. Sign up, configure your company, and start running
              operations.
            </p>
          </div>

          <div class="row g-4 justify-content-center">
            <div class="col-md-4">
              <div class="step-card h-100">
                <div class="step-num">1</div>
                <h5 class="mb-2">Create your company</h5>
                <p class="text-muted small mb-0">
                  Register your company and create your admin profile in
                  minutes.
                </p>
              </div>
            </div>

            <div class="col-md-4">
              <div class="step-card h-100">
                <div class="step-num">2</div>
                <h5 class="mb-2">Choose a plan</h5>
                <p class="text-muted small mb-0">
                  Pick a plan for your modules and size. Trial starts
                  immediately.
                </p>
              </div>
            </div>

            <div class="col-md-4">
              <div class="step-card h-100">
                <div class="step-num">3</div>
                <h5 class="mb-2">Invite your team</h5>
                <p class="text-muted small mb-0">
                  Add employees, configure payroll, and start tracking work and
                  revenue.
                </p>
              </div>
            </div>
          </div>

          <!-- Testimonials -->
          <div class="mt-5">
            <div class="row g-4">
              <div class="col-md-6">
                <div class="testimonial-card h-100">
                  <div class="d-flex align-items-start gap-3">
                    <span class="avatar-pill">A</span>
                    <div>
                      <div class="fw-bold">
                        “We finally stopped juggling tools.”
                      </div>
                      <div class="text-muted small">
                        HR, payroll, attendance and projects are now in one
                        place — approvals are fast, and reporting is clear.
                      </div>
                      <div class="mt-3 small fw-semibold text-muted">
                        Operations Lead · Nairobi
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="testimonial-card h-100">
                  <div class="d-flex align-items-start gap-3">
                    <span class="avatar-pill">K</span>
                    <div>
                      <div class="fw-bold">
                        “Payroll generation takes seconds, not hours or days.”
                      </div>
                      <div class="text-muted small">
                        The flow is straightforward. Payslips and exports are
                        ready when we need them.
                      </div>
                      <div class="mt-3 small fw-semibold text-muted">
                        HR Manager · Mombasa
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- PRICING -->
      <section id="pricing" class="py-5">
        <div class="container">
          <div class="text-center mb-4">
            <span
              class="badge bg-soft-primary text-primary fw-semibold px-3 py-2 rounded-pill mb-2"
            >
              <i class="ti ti-tag me-1"></i> Pricing
            </span>
            <h2 class="fw-bold mb-2">Simple pricing for growing teams</h2>
            <p class="text-muted mb-0">
              Start on a free trial. Upgrade anytime as you scale.
            </p>
          </div>

          <div class="card border-0 shadow-sm pricing-shell">
            <div class="card-body p-4 p-lg-5">
              <!-- Loading -->
              <div
                v-if="loading"
                class="d-flex justify-content-center align-items-center py-5"
              >
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>

              <!-- Empty -->
              <div v-else-if="!sortedPlans.length" class="text-center py-5">
                <h5 class="mb-2">No plans available</h5>
                <p class="text-muted mb-0">
                  Plans will appear here once available.
                </p>
              </div>

              <!-- Plans cards -->
              <div v-else class="row g-4 justify-content-center">
                <div
                  v-for="plan in sortedPlans"
                  :key="plan.id"
                  class="col-lg-4 col-md-6 d-flex"
                >
                  <div
                    class="plan-card card border-0 shadow-sm flex-fill"
                    :class="{ 'plan-card--recommended': plan.is_recommended }"
                  >
                    <div class="card-body d-flex flex-column p-4">
                      <div
                        class="d-flex justify-content-between align-items-start mb-3"
                      >
                        <div>
                          <div class="fw-bold  mb-1">{{ plan.name }}</div>
                          <div class="text-muted small">
                            Great for teams who want
                            <span class="fw-semibold">clarity and speed</span>.
                          </div>
                        </div>
                        <span
                          v-if="plan.is_recommended"
                          class="badge bg-primary rounded-pill"
                        >
                          Most Popular
                        </span>
                      </div>

                      <div class="price-line mb-3">
                        <div
                          v-if="isContactSalesPlan(plan)"
                          class="annual-license-price"
                        >
                          <div class="display-6 fw-bold mb-0">
                            Contact Sales
                          </div>
                          <div class="text-muted small mt-1">
                            Custom commercial terms for your organization.
                          </div>
                        </div>
                        <div v-else class="d-flex align-items-end gap-2">
                          <div class="display-6 fw-bold mb-0">
                            {{ currencySymbol(plan.currency)
                            }}{{ displayPrice(plan) }}
                          </div>
                        </div>
                        <div
                          v-if="
                            !isContactSalesPlan(plan) &&
                            hasDiscount(plan)
                          "
                          class="text-success small fw-semibold mt-1"
                        >
                          Save {{ currencySymbol(plan.currency)
                          }}{{ planDiscountAmount(plan) }}
                          <span v-if="planDiscountPercent(plan) > 0">
                            ({{ planDiscountPercent(plan) }}%)
                          </span>
                          on the configured plan price
                        </div>
                        <!-- <div class="text-muted small">
                          Billed as {{ billingLabel }} (display conversion
                          only).
                        </div> -->
                      </div>

                      <div class="plan-features rounded-4 mb-3 p-3">
                        <div
                          class="d-flex align-items-center justify-content-between mb-2"
                        >
                          <div class="fw-semibold">What’s included</div>
                        </div>

                        <div class="small">
                          <span
                            v-for="cat in pricingHighlights(plan)"
                            :key="`${plan.id}-${cat}`"
                            class="d-flex align-items-center mb-2"
                          >
                            <i
                              class="ti ti-discount-check-filled text-success me-2"
                            ></i>
                            {{ cat }}
                          </span>

                          <span
                            v-if="
                              !pricingHighlights(plan).length
                            "
                            class="text-muted d-flex align-items-center"
                          >
                            <i
                              class="ti ti-circle-x-filled text-danger me-2"
                            ></i>
                            No specific features configured
                          </span>

                        </div>
                      </div>

                      <component
                        :is="isContactSalesPlan(plan) ? 'button' : 'router-link'"
                        class="btn w-100 mt-auto"
                        :class="
                          plan.is_recommended ? 'btn-primary' : 'btn-dark'
                        "
                        :type="isContactSalesPlan(plan) ? 'button' : null"
                        :to="
                          isContactSalesPlan(plan)
                            ? null
                            : { path: '/register', query: { plan_id: plan.id } }
                        "
                        @click="
                          isContactSalesPlan(plan)
                            ? openContactSalesModal(plan)
                            : null
                        "
                      >
                        Start with {{ plan.name }}
                        <i class="ti ti-arrow-right ms-2"></i>
                      </component>

                      <div class="text-center text-muted small mt-3">
                        <template v-if="isContactSalesPlan(plan)">
                          <i class="ti ti-briefcase me-1"></i> Tailored onboarding
                          · Dedicated commercial engagement
                        </template>
                        <template v-else>
                          <i class="ti ti-shield-check me-1"></i> No credit card required
                          · Cancel anytime
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- /Plans cards -->

              <!-- Compare table -->
              <div v-if="sortedPlans.length" class="mt-5">
                <div class="text-center mb-3">
                  <h4 class="fw-bold mb-1">Compare plans</h4>
                  <p class="text-muted mb-0 small">
                    Quick overview based on your configured plan limits + module
                    groupings.
                  </p>
                </div>

                <div class="table-responsive compare-table-wrap">
                  <table class="table align-middle compare-table mb-0">
                    <thead>
                      <tr>
                        <th class="text-muted small fw-semibold">
                          What you get
                        </th>
                        <th
                          v-for="p in sortedPlans"
                          :key="p.id"
                          class="text-center"
                        >
                          <div class="fw-bold">{{ p.name }}</div>
                          <div class="text-muted small">
                            <template v-if="isContactSalesPlan(p)">
                              Contact Sales
                            </template>
                            <template v-else>
                              {{ currencySymbol(p.currency)
                              }}{{ displayPrice(p) }}/plan
                            </template>
                          </div>
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr>
                        <td class="fw-semibold">Plan highlights</td>
                        <td
                          v-for="p in sortedPlans"
                          :key="p.id"
                          class="text-center"
                        >
                          <div
                            class="d-flex flex-column gap-1 align-items-center"
                          >
                            <span
                              v-for="cat in pricingHighlights(p)"
                              :key="`${p.id}-${cat}`"
                              class="badge bg-light text-dark border"
                            >
                              {{ cat }}
                            </span>
                            <span
                              v-if="!pricingHighlights(p).length"
                              class="text-muted small"
                              >—</span
                            >
                          </div>
                        </td>
                      </tr>

                      <!-- <tr>
                        <td class="fw-semibold">Customers limit</td>
                        <td
                          v-for="p in sortedPlans"
                          :key="p.id"
                          class="text-center"
                        >
                          <span class="text-muted">{{
                            p.max_customers || "—"
                          }}</span>
                        </td>
                      </tr>

                      <tr>
                        <td class="fw-semibold">Invoices limit</td>
                        <td
                          v-for="p in sortedPlans"
                          :key="p.id"
                          class="text-center"
                        >
                          <span class="text-muted">{{
                            p.max_invoices || "—"
                          }}</span>
                        </td>
                      </tr>

                      <tr>
                        <td class="fw-semibold">Products limit</td>
                        <td
                          v-for="p in sortedPlans"
                          :key="p.id"
                          class="text-center"
                        >
                          <span class="text-muted">{{
                            p.max_products || "—"
                          }}</span>
                        </td>
                      </tr>

                      <tr>
                        <td class="fw-semibold">Suppliers limit</td>
                        <td
                          v-for="p in sortedPlans"
                          :key="p.id"
                          class="text-center"
                        >
                          <span class="text-muted">{{
                            p.max_suppliers || "—"
                          }}</span>
                        </td>
                      </tr> -->

                      <tr>
                        <td class="fw-semibold">Free trial</td>
                        <td
                          v-for="p in sortedPlans"
                          :key="p.id"
                          class="text-center"
                        >
                          <span
                            v-if="trialDisplay(p)"
                            class="badge bg-success-subtle text-success fw-semibold"
                          >
                            {{ trialDisplay(p) }}
                          </span>
                          <span v-else class="text-muted">—</span>
                        </td>
                      </tr>

                      <tr>
                        <td class="fw-semibold">Start</td>
                        <td
                          v-for="p in sortedPlans"
                          :key="p.id"
                          class="text-center"
                        >
                          <component
                            :is="isContactSalesPlan(p) ? 'button' : 'router-link'"
                            class="btn btn-sm"
                            :class="
                              p.is_recommended ? 'btn-primary' : 'btn-dark'
                            "
                            :type="isContactSalesPlan(p) ? 'button' : null"
                            :to="
                              isContactSalesPlan(p)
                                ? null
                                : {
                                    path: '/register',
                                    query: { plan_id: p.id },
                                  }
                            "
                            @click="
                              isContactSalesPlan(p)
                                ? openContactSalesModal(p)
                                : null
                            "
                          >
                            {{ isContactSalesPlan(p) ? "Contact Sales" : "Choose" }}
                          </component>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <!-- /Compare table -->
            </div>
          </div>
        </div>
      </section>

      <!-- FAQ -->
      <section id="faq" class="py-5 bg-white">
        <div class="container">
          <div class="text-center mb-4">
            <span
              class="badge bg-soft-primary text-primary fw-semibold px-3 py-2 rounded-pill mb-2"
            >
              <i class="ti ti-help-circle me-1"></i> FAQ
            </span>
            <h2 class="fw-bold mb-2">Questions, answered</h2>
            <p class="text-muted mb-0">
              Everything you need to know before you start.
            </p>
          </div>

          <div class="row g-4 justify-content-center">
            <div class="col-lg-10">
              <div class="accordion" id="publicFaq">
                <div
                  class="accordion-item border-0 shadow-sm mb-3 rounded-4 overflow-hidden"
                >
                  <h2 class="accordion-header" id="q1">
                    <button
                      class="accordion-button fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#a1"
                    >
                      Do you support Kenya payroll and statutory deductions?
                    </button>
                  </h2>
                  <div
                    id="a1"
                    class="accordion-collapse collapse show"
                    data-bs-parent="#publicFaq"
                  >
                    <div class="accordion-body text-muted">
                      Yes — the platform is designed for localized payroll
                      flows, compliance-ready outputs, and exportable reports.
                      (You can extend rules as your business needs grow.)
                    </div>
                  </div>
                </div>

                <div
                  class="accordion-item border-0 shadow-sm mb-3 rounded-4 overflow-hidden"
                >
                  <h2 class="accordion-header" id="q2">
                    <button
                      class="accordion-button collapsed fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#a2"
                    >
                      Can I use only some modules (e.g., HR + Payroll only)?
                    </button>
                  </h2>
                  <div
                    id="a2"
                    class="accordion-collapse collapse"
                    data-bs-parent="#publicFaq"
                  >
                    <div class="accordion-body text-muted">
                      Yes. Plans can include specific modules. Start small, then
                      enable more modules as needed.
                    </div>
                  </div>
                </div>

                <div
                  class="accordion-item border-0 shadow-sm mb-3 rounded-4 overflow-hidden"
                >
                  <h2 class="accordion-header" id="q3">
                    <button
                      class="accordion-button collapsed fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#a3"
                    >
                      Is my data secure?
                    </button>
                  </h2>
                  <div
                    id="a3"
                    class="accordion-collapse collapse"
                    data-bs-parent="#publicFaq"
                  >
                    <div class="accordion-body text-muted">
                      Data is company-scoped, role-protected, and designed with
                      auditability in mind. You control access per role and
                      user.
                    </div>
                  </div>
                </div>

                <div
                  class="accordion-item border-0 shadow-sm rounded-4 overflow-hidden"
                >
                  <h2 class="accordion-header" id="q4">
                    <button
                      class="accordion-button collapsed fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#a4"
                    >
                      Can I migrate from Excel / other tools?
                    </button>
                  </h2>
                  <div
                    id="a4"
                    class="accordion-collapse collapse"
                    data-bs-parent="#publicFaq"
                  >
                    <div class="accordion-body text-muted">
                      Yes. Import/export workflows can be supported — start with
                      employees and payroll structure, then extend into CRM and
                      finance.
                    </div>
                  </div>
                </div>
              </div>

              <div class="cta-card mt-5">
                <div class="row g-3 align-items-center">
                  <div class="col-md-8">
                    <div class="fw-bold">
                      Ready to run your company on one system?
                    </div>
                    <div class="text-muted">
                      Start your free trial and invite your team in minutes.
                    </div>
                  </div>
                  <div class="col-md-4 text-md-end">
                    <router-link
                      to="/register"
                      class="btn btn-primary btn-lg px-4"
                    >
                      Start Free Trial <i class="ti ti-arrow-right ms-2"></i>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- CONTACT SALES MODAL -->
    <div
      class="modal fade dq-demo"
      id="contact_sales_modal"
      tabindex="-1"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered dq-demo-dialog">
        <div class="modal-content dq-demo-card dq-sales-card">
          <div class="modal-header dq-demo-header">
            <div class="d-flex align-items-start gap-3">
              <span class="dq-demo-icon dq-sales-icon">
                <i class="ti ti-briefcase"></i>
              </span>
              <div class="flex-grow-1">
                <h5 class="modal-title mb-1">Contact Sales</h5>
                <div class="dq-demo-subtitle">
                  <span v-if="selectedSalesPlan">
                    {{ selectedSalesPlan.name }} is available through a tailored sales engagement.
                  </span>
                  <span v-else>
                    This plan is available through a tailored sales engagement.
                  </span>
                </div>
              </div>
            </div>

            <button
              type="button"
              class="btn-close dq-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>

          <div class="modal-body dq-demo-body">
            <div class="dq-sales-hero mb-4">
              <div class="dq-sales-eyebrow mb-2">Enterprise Plan</div>
              <h4 class="mb-2">
                Let's structure the right package for your organization.
              </h4>
              <p class="text-muted mb-0">
                Contact our sales team for pricing, rollout planning, and commercial terms.
              </p>
            </div>

            <div class="row g-3">
              <div class="col-md-6">
                <a
                  class="dq-sales-contact text-decoration-none"
                  href="mailto:info@donequick-technologicalworld.com"
                >
                  <span class="dq-sales-contact-icon">
                    <i class="ti ti-mail"></i>
                  </span>
                  <span>
                    <span class="dq-sales-contact-label">Email</span>
                    <strong>info@donequick-technologicalworld.com</strong>
                  </span>
                </a>
              </div>
              <div class="col-md-6">
                <a
                  class="dq-sales-contact text-decoration-none"
                  href="tel:0720263910"
                >
                  <span class="dq-sales-contact-icon">
                    <i class="ti ti-phone-call"></i>
                  </span>
                  <span>
                    <span class="dq-sales-contact-label">Phone</span>
                    <strong>0720263910</strong>
                  </span>
                </a>
              </div>
            </div>

            <div class="dq-mini-note mt-4">
              <i class="ti ti-info-circle"></i>
              Enterprise plans are handled directly by sales instead of self-service signup.
            </div>
          </div>

          <div class="modal-footer dq-demo-footer">
            <button
              type="button"
              class="btn btn-outline-secondary px-4"
              data-bs-dismiss="modal"
            >
              Close
            </button>
            <a
              class="btn btn-dark px-4"
              href="mailto:info@donequick-technologicalworld.com"
            >
              Email Sales <i class="ti ti-arrow-up-right ms-2"></i>
            </a>
            <a class="btn btn-primary px-4" href="tel:0720263910">
              Call Sales <i class="ti ti-phone ms-2"></i>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- REQUEST DEMO MODAL -->
    <div
      class="modal fade dq-demo"
      id="request_demo"
      tabindex="-1"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg dq-demo-dialog">
        <div class="modal-content dq-demo-card">
          <!-- Header -->
          <div class="modal-header dq-demo-header">
            <div class="d-flex align-items-start gap-3">
              <span class="dq-demo-icon">
                <i class="ti ti-calendar-event"></i>
              </span>
              <div class="flex-grow-1">
                <h5 class="modal-title mb-1">Request a Demo</h5>
                <div class="dq-demo-subtitle">
                  Schedule a quick walkthrough — we’ll contact you via your
                  preferred method.
                </div>
              </div>
            </div>

            <button
              type="button"
              class="btn-close dq-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>

          <!-- Body -->
          <div class="modal-body dq-demo-body">
            <!-- success state -->
            <div v-if="success" class="dq-success">
              <div class="dq-success-badge">
                <i class="ti ti-circle-check"></i>
              </div>
              <h5 class="mb-1">Request sent!</h5>
              <p class="text-muted mb-3">
                We’ll reach out via
                <b>{{
                  demo.preferred_contact === "phone" ? "Phone" : "Email"
                }}</b>
                to confirm your demo time.
              </p>
              <div class="d-flex gap-2 justify-content-center">
                <button
                  class="btn btn-dark px-4"
                  type="button"
                  @click="closeDemoModal"
                >
                  Done
                </button>
                <button
                  class="btn btn-outline-secondary px-4"
                  type="button"
                  @click="resetDemo"
                >
                  Send another request
                </button>
              </div>
            </div>

            <!-- form -->
            <form v-else @submit.prevent="submitDemoRequest" class="row g-3">
              <div class="col-12">
                <div class="dq-mini-note">
                  <i class="ti ti-lock"></i>
                  Your details are used only to contact you about the demo.
                </div>
              </div>

              <div class="col-md-6">
                <label class="form-label fw-semibold"
                  >Full Name <span class="text-danger">*</span></label
                >
                <input
                  v-model.trim="demo.full_name"
                  type="text"
                  class="form-control dq-input"
                  :class="{ 'is-invalid': touched && !demo.full_name }"
                  placeholder="e.g. Jane Doe"
                  required
                />
              </div>

              <div class="col-md-6">
                <label class="form-label fw-semibold"
                  >Company Name <span class="text-danger">*</span></label
                >
                <input
                  v-model.trim="demo.company_name"
                  type="text"
                  class="form-control dq-input"
                  :class="{ 'is-invalid': touched && !demo.company_name }"
                  placeholder="e.g. Example Technologies"
                  required
                />
              </div>

              <div class="col-md-6">
                <label class="form-label fw-semibold"
                  >Work Email <span class="text-danger">*</span></label
                >
                <input
                  v-model.trim="demo.email"
                  type="email"
                  class="form-control dq-input"
                  :class="{
                    'is-invalid': touched && !isValidEmail(demo.email),
                  }"
                  placeholder="name@company.com"
                  required
                />
                <div
                  v-if="touched && demo.email && !isValidEmail(demo.email)"
                  class="invalid-feedback"
                >
                  Enter a valid email address.
                </div>
              </div>

              <div class="col-md-6">
                <label class="form-label fw-semibold"
                  >Preferred Contact Method
                  <span class="text-danger">*</span></label
                >
                <div class="dq-seg">
                  <button
                    type="button"
                    class="dq-seg-btn"
                    :class="{ active: demo.preferred_contact === 'email' }"
                    @click="demo.preferred_contact = 'email'"
                  >
                    <i class="ti ti-mail"></i> Email
                  </button>
                  <button
                    type="button"
                    class="dq-seg-btn"
                    :class="{ active: demo.preferred_contact === 'phone' }"
                    @click="demo.preferred_contact = 'phone'"
                  >
                    <i class="ti ti-phone"></i> Phone
                  </button>
                </div>
              </div>

              <div class="col-md-6" v-if="demo.preferred_contact === 'phone'">
                <label class="form-label fw-semibold"
                  >Phone Number <span class="text-danger">*</span></label
                >
                <input
                  v-model.trim="demo.phone"
                  type="tel"
                  class="form-control dq-input"
                  :class="{
                    'is-invalid':
                      touched &&
                      demo.preferred_contact === 'phone' &&
                      !demo.phone,
                  }"
                  placeholder="+2547..."
                  required
                />
                <div
                  v-if="
                    touched && demo.preferred_contact === 'phone' && !demo.phone
                  "
                  class="invalid-feedback"
                >
                  Phone is required when contact method is Phone.
                </div>
              </div>

              <div class="col-md-6">
                <label class="form-label fw-semibold"
                  >Company Size <span class="text-danger">*</span></label
                >
                <select
                  v-model="demo.company_size"
                  class="form-select dq-input"
                  :class="{ 'is-invalid': touched && !demo.company_size }"
                  required
                >
                  <option value="">Select...</option>
                  <option value="1-10">1–10</option>
                  <option value="11-50">11–50</option>
                  <option value="51-200">51–200</option>
                  <option value="200+">200+</option>
                </select>
              </div>

              <div class="col-md-4">
                <label class="form-label fw-semibold"
                  >Preferred Date <span class="text-danger">*</span></label
                >
                <input
                  v-model="demo.preferred_date"
                  type="date"
                  class="form-control dq-input"
                  :class="{ 'is-invalid': touched && !demo.preferred_date }"
                  required
                />
              </div>

              <div class="col-md-4">
                <label class="form-label fw-semibold"
                  >Preferred Time <span class="text-danger">*</span></label
                >
                <input
                  v-model="demo.preferred_time"
                  type="time"
                  class="form-control dq-input"
                  :class="{ 'is-invalid': touched && !demo.preferred_time }"
                  required
                />
              </div>

              <div class="col-md-4">
                <label class="form-label fw-semibold"
                  >Timezone <span class="text-danger">*</span></label
                >
                <select
                  v-model="demo.timezone"
                  class="form-select dq-input"
                  required
                >
                  <option value="Africa/Nairobi">Africa/Nairobi (EAT)</option>
                   <option value="Africa/Kampala">Africa/Kampala (EAT)</option>
                    <option value="Africa/Dar es Salaam">Africa/Dar es Salaam (EAT)</option>
                     <option value="Africa/Kigali">Africa/Kigali (CAT)</option>
                      <option value="Africa/Johannesburg">Africa/Johannesburg (SAST)</option>
                       <option value="Europe/London">Europe/London (GMT/BST)</option>
                        <option value="UTC">UTC</option>
                         <option value="America/New_York">America/New_York (ET)</option>
                          <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                </select>
              </div>


              <div class="col-12">
                <label class="form-label fw-semibold">Notes (optional)</label>
                <textarea
                  v-model.trim="demo.notes"
                  class="form-control dq-input"
                  rows="3"
                  placeholder="What are you trying to improve? Any current tools you use?"
                ></textarea>
              </div>

              <div class="col-12">
                <label class="d-flex gap-2 align-items-start dq-consent">
                  <input
                    v-model="demo.consent"
                    type="checkbox"
                    class="form-check-input mt-1"
                    required
                  />
                  <span class="text-muted small">
                    I agree to be contacted about DoneQuick HRM & Payroll based on the
                    details I submit.
                  </span>
                </label>
              </div>

              <div class="col-12">
                <div v-if="error" class="alert alert-danger rounded-4 mb-0">
                  <i class="ti ti-alert-triangle me-1"></i> {{ error }}
                </div>
              </div>
            </form>
          </div>

          <!-- Footer -->
          <div v-if="!success" class="modal-footer dq-demo-footer">
            <button
              type="button"
              class="btn btn-outline-secondary px-4"
              data-bs-dismiss="modal"
              :disabled="submitting"
            >
              Cancel
            </button>

            <button
              type="button"
              class="btn btn-dark px-4"
              @click="resetDemo"
              :disabled="submitting"
            >
              Reset
            </button>

            <button
              class="btn btn-primary px-4"
              type="button"
              @click="submitDemoRequest"
              :disabled="submitting"
            >
              <span
                v-if="submitting"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Submit Request <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <footer class="py-4 border-top bg-white">
      <div
        class="container d-flex flex-wrap justify-content-between align-items-center gap-2"
      >
        <p class="mb-0 text-muted">
          2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll
        </p>
        <p class="mb-0 text-muted small">
          Built for teams in Kenya · East Africa · Beyond
        </p>
      </div>
    </footer>

    <workspace-sign-in-modal ref="workspaceSignInModal" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeMount, watch, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import HrPayrollMark from "@/components/branding/hr-payroll-mark.vue";
import WorkspaceSignInModal from "@/components/auth/workspace-sign-in-modal.vue";
import publicPlanService from "@/services/public-plan.service";
import { Modal } from "bootstrap";
import { toast } from "@/utils/toast";
import {
  getPlanDiscountAmount,
  getPlanDiscountPercent,
  getPlanDisplayPrice,
  hasPlanDiscount,
} from "@/utils/planPricing";
import { isTenancyFrontendEnabled } from "@/utils/hostname";

const route = useRoute();
const router = useRouter();
const workspaceSignInModal = ref(null);
let workspaceModalOpening = false;

function openWorkspaceSignIn() {
  if (!isTenancyFrontendEnabled()) {
    router.push("/login");
    return;
  }
  workspaceSignInModal.value?.open();
}

async function maybeOpenWorkspaceFromQuery() {
  if (route.query.workspace !== "required" || workspaceModalOpening) return;
  workspaceModalOpening = true;
  try {
    await nextTick();
    openWorkspaceSignIn();
    const nextQuery = { ...route.query };
    delete nextQuery.workspace;
    router.replace({ path: "/", query: nextQuery });
  } finally {
    workspaceModalOpening = false;
  }
}

/** -------------------- pricing logic (unchanged) -------------------- **/
const plans = ref([]);
const loading = ref(false);
const tabsWrapRef = ref(null);
const selectedSalesPlan = ref(null);

function onTabsWheel(e) {
  const el = tabsWrapRef.value;
  if (!el) return;

  // Only hijack wheel when horizontal scrolling is possible
  const canScrollX = el.scrollWidth > el.clientWidth;
  if (!canScrollX) return;

  // Convert vertical wheel to horizontal scroll
  // (trackpad users may already have deltaX)
  const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;

  e.preventDefault();
  el.scrollLeft += delta;
}

onMounted(async () => {
  loading.value = true;
  try {
    const res = await publicPlanService.getAll();
    plans.value = Array.isArray(res.data) ? res.data : res.data || [];
  } catch (e) {
    console.error(e);
    toast("Failed to load plans", "error");
  } finally {
    loading.value = false;
  }

  const el = tabsWrapRef.value;
  if (!el) return;
  // passive:false is REQUIRED so preventDefault works
  el.addEventListener("wheel", onTabsWheel, { passive: false });
});

onBeforeMount(() => {
  const el = tabsWrapRef.value;
  if (!el) return;
  el.removeEventListener("wheel", onTabsWheel);
});

const sortedPlans = computed(() => {
  return [...plans.value].sort((a, b) => {
    const pa = a.position ?? 999;
    const pb = b.position ?? 999;
    if (pa !== pb) return pa - pb;
    return (a.id || 0) - (b.id || 0);
  });
});

const priceForPlan = (plan) => getPlanDisplayPrice(plan);

const formatPrice = (value) =>
  new Intl.NumberFormat("en-KE", {
    minimumFractionDigits: Number(value) % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const displayPrice = (plan) => formatPrice(priceForPlan(plan));

const isContactSalesPlan = (plan) => !!plan?.is_contact_sales;

const planDiscountAmount = (plan) => formatPrice(getPlanDiscountAmount(plan));

const planDiscountPercent = (plan) => {
  const percent = getPlanDiscountPercent(plan);
  return Number(percent.toFixed(percent >= 10 ? 0 : 1));
};

const hasDiscount = (plan) => !isContactSalesPlan(plan) && hasPlanDiscount(plan);

// --- Feature group mapping (for display only) -----------------------------
const FEATURE_GROUPS = [
  { label: "Project Management", keys: ["projects", "tasks", "task_board"] },
  {
    label: "HRM & Payroll",
    keys: [
      "hrm_employee",
      "hrm_payroll",
      "hrm_attendance",
      "hrm_performance",
      "hrm_training",
      "hrm_promotion",
      "hrm_resignation",
      "hrm_termination",
      "hrm_tickets",
      "hrm_holidays",
    ],
  },

  {
    label: "Loans & Savings",
    keys: ["loans", "savings"],
  },
  {
    label: "CRM",
    keys: [
      "crm_contacts",
      "crm_clients",
      "crm_deals",
      "crm_leads",
      "crm_pipeline",
      "crm_analytics",
      "crm_activities",
    ],
  },
  { label: "Finance & Accounts", keys: ["fa_sales", "fa_accounting"] },
  {
    label: "Administration & Reporting",
    keys: [
      "admin_assets",
      "admin_help_support",
      "admin_reports",
      "admin_settings",
    ],
  },
];

const HIDDEN_PRICING_FEATURES = [
  "HRM & Payroll",
  "Loans & Savings",
  "Administration & Reporting",
  "Finance & Accounts",
  "Project Management",
  "Projects",
  "CRM",
  "Free trial available based on configured duration",
  "Initial support included free",
];

const featureCategoriesForPlan = (plan) => {
  if (Array.isArray(plan?.marketing_features) && plan.marketing_features.length) {
    return plan.marketing_features.filter(
      (item) => !HIDDEN_PRICING_FEATURES.includes(String(item || "").trim())
    );
  }
  return [];
};

const supportDisplay = (plan) => {
  const months = Number(plan?.support_period_months || 0);
  if (!months) return "";
  return `Initial ${months} ${months === 1 ? "month" : "months"} support free`;
};

const trialDisplay = (plan) => {
  if (!plan?.allow_trial) return "";
  const value = Number(plan?.trial_interval_value || plan?.trial_days || 0);
  const unit = String(plan?.trial_interval_unit || "days").toLowerCase();
  if (!value) return "";
  const normalizedUnit = value === 1 ? unit.replace(/s$/, "") : unit;
  return `${value} ${normalizedUnit} free trial`;
};

const pricingHighlights = (plan) => {
  const features = [...featureCategoriesForPlan(plan)];
  const support = supportDisplay(plan);
  const trial = trialDisplay(plan);
  if (support) features.push(support);
  if (trial) features.push(trial);
  return features.filter(Boolean);
};

const currencySymbol = (code) => {
  if (!code) return "";
  const map = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    KES: "KES ",
    UGX: "UGX ",
    TZS: "TZS ",
    RWF: "RWF ",
  };
  return map[code] ?? code + " ";
};

/** -------------------- hero preview tabs -------------------- **/
const activeTab = ref("hr");
const FEATURE_TABS = [
  { key: "hr", label: "HR", icon: "ti ti-users" },
  { key: "payroll", label: "Payroll", icon: "ti ti-receipt-tax" },
  {key:  "loansSavings", label: "Loans & Savings", icon: "ti ti-wallet" },
  { key: "attendance", label: "Attendance", icon: "ti ti-clock" },
  { key: "crm", label: "CRM", icon: "ti ti-briefcase" },
  { key: "projects", label: "Projects", icon: "ti ti-layout-kanban" },
  { key: "finance", label: "Finance", icon: "ti ti-calculator" },
];

const TAB_CONTENT = {
  hr: {
    title: "HR & People Operations",
    subtitle: "Employee profiles, departments, onboarding and approvals.",
    chips: ["Employee Directory", "Onboarding", "Documents", "Approvals"],
  },
  payroll: {
    title: "Payroll & Compliance",
    subtitle: "Run payroll fast with compliance-ready outputs.",
    chips: ["PAYE/SHA/NSSF", "Payslips", "P9 Exports", "Payroll Runs"],
  },
  loansSavings: {
    title: "Loans & Savings Management",
    subtitle: "Manage employee loans, savings and deductions.",
    chips: ["Loan Tracking", "Savings Plans", "Deductions", "Reports"],
  },
  attendance: {
    title: "Attendance & Scheduling",
    subtitle: "Shifts, schedules, punch-in/out and summaries.",
    chips: ["Shifts", "Schedules", "Punch In/Out", "Timesheets"],
  },
  crm: {
    title: "CRM & Sales Pipeline",
    subtitle: "Leads, deals and pipelines connected to invoicing.",
    chips: ["Leads", "Deals", "Pipeline", "Activities"],
  },
  projects: {
    title: "Projects & Delivery",
    subtitle: "Kanban boards, tasks and team execution tracking.",
    chips: ["Boards", "Tasks", "Owners", "Progress"],
  },
  finance: {
    title: "Finance & Accounting",
    subtitle: "Invoices, expenses and reporting across modules.",
    chips: ["Invoices", "Expenses", "Reports", "Accounting"],
  },
};

const activeTabMeta = computed(
  () => TAB_CONTENT[activeTab.value] || TAB_CONTENT.hr
);

/** -------------------- module deep dive -------------------- **/
const deepActive = ref("hr");

const DEEP_DIVE = [
  {
    key: "hr",
    label: "HR & People",
    icon: "ti ti-users",
    tagline: "Onboarding, records, approvals",
  },
  {
    key: "payroll",
    label: "Payroll",
    icon: "ti ti-receipt-tax",
    tagline: "Runs, payslips, compliance",
  },
  {
    key: "loansSavings",
    label: "Loans & Savings",
    icon: "ti ti-wallet",
    tagline: "Loans, savings, deductions",
  },
  {
    key: "attendance",
    label: "Attendance",
    icon: "ti ti-clock",
    tagline: "Shifts, schedules, timesheets",
  },
  {
    key: "crm",
    label: "CRM",
    icon: "ti ti-briefcase",
    tagline: "Leads, deals, pipeline",
  },
  {
    key: "projects",
    label: "Projects",
    icon: "ti ti-layout-kanban",
    tagline: "Boards, tasks, delivery",
  },
  {
    key: "finance",
    label: "Finance",
    icon: "ti ti-calculator",
    tagline: "Invoices, expenses, reporting",
  },
];

const DEEP_CONTENT = {
  hr: {
    icon: "ti ti-users",
    title: "HR & People",
    badge: "Core",
    description:
      "Centralize employee data, streamline onboarding, and run approvals with clarity. Keep your HR records clean and always up to date.",
    capabilities: [
      "Employee profiles (personal, job, documents)",
      "Departments, designations and organization structure",
      "Onboarding and record management",
      "Approvals and access control (role-based)",
    ],
    reports: [
      "Employee register",
      "Department summaries",
      "Onboarding status",
      "HR export-ready lists",
    ],
    connected: ["Payroll", "Attendance", "Projects", "Administration"],
    preview: [
      {
        title: "Employee Directory",
        note: "Quick search, filters, and profile completeness.",
        chips: ["Search & Filter", "Profile Cards", "Documents"],
      },
      {
        title: "Approvals",
        note: "Standardized flows for HR actions and changes.",
        chips: ["Requests", "Approvals", "Audit Trail"],
      },
    ],
  },
  payroll: {
    icon: "ti ti-receipt-tax",
    title: "Payroll",
    badge: "Compliance",
    description:
      "Run payroll in a clear, repeatable process—calculate, review, approve and export. Get payslips and compliance-friendly outputs.",
    capabilities: [
      "Payroll runs with review and approvals",
      "Statutory deductions support (PAYE, SHA, NSSF workflows)",
      "Payslips and export-ready summaries",
      "Allowances, benefits and deductions structure",
    ],
    reports: [
      "Payroll summary",
      "Payslips",
      "P9-ready exports",
      "Statutory summaries",
    ],
    connected: ["HR", "Attendance", "Finance", "Administration"],
    preview: [
      {
        title: "Payroll Run",
        note: "Run, review exceptions, then approve.",
        chips: ["Run", "Review", "Approve", "Export"],
      },
      {
        title: "Payslips",
        note: "Employee-ready payslips and downloadable outputs.",
        chips: ["Payslip PDF", "Email-ready", "History"],
      },
    ],
  },

  loansSavings: {
    icon: "ti ti-wallet",
    title: "Loans & Savings",
    badge: "Benefits",
    description:
      "Manage employee loans and savings plans with clear tracking. Automate deductions and generate reports for better visibility.",
    capabilities: [
      "Employee loan tracking and management",
      "Savings plans and contributions",
      "Automated payroll deductions",
      "Reporting on loans and savings status",
    ],
    reports: [
      "Loan summaries",
      "Savings plan reports",
      "Deduction schedules",
      "Employee statements",
    ],
    connected: ["HR", "Payroll", "Finance", "Reports"],
    preview: [
      {
        title: "Loan Management",
        note: "Track loan amounts, repayments, and balances.",
        chips: ["Loan Details", "Repayment Schedule", "Balances"],
      },
      {
        title: "Savings Plans",
        note: "Manage employee savings contributions and status.",
        chips: ["Plan Setup", "Contributions", "Status Reports"],
      },
    ],
  },
  attendance: {
    icon: "ti ti-clock",
    title: "Attendance",
    badge: "Operations",
    description:
      "Track attendance accurately with shifts and schedules. Capture punch in/out, manage leave, and prepare timesheets for payroll and projects.",
    capabilities: [
      "Shifts and schedules",
      "Punch in/out with notes and break tracking",
      "Attendance summaries and approvals",
      "Timesheet-friendly records",
    ],
    reports: [
      "Daily attendance",
      "Late/early summaries",
      "Shift coverage",
      "Timesheet exports",
    ],
    connected: ["HR", "Payroll", "Projects", "Reports"],
    preview: [
      {
        title: "Schedules",
        note: "Assign shifts, manage coverage, reduce gaps.",
        chips: ["Shifts", "Schedules", "Coverage"],
      },
      {
        title: "Attendance Logs",
        note: "Clear logs to support payroll accuracy.",
        chips: ["Punch In", "Punch Out", "Breaks"],
      },
    ],
  },
  crm: {
    icon: "ti ti-briefcase",
    title: "CRM",
    badge: "Growth",
    description:
      "Track leads and deals with a consistent pipeline. Stay on top of follow-ups, measure conversion, and connect sales activity to projects and billing.",
    capabilities: [
      "Leads, deals and pipeline stages",
      "Tags, activities and reminders",
      "Analytics for conversion and performance",
      "Client data connected to billing and delivery",
    ],
    reports: [
      "Pipeline overview",
      "Leads by source",
      "Won/Lost summaries",
      "Activity performance",
    ],
    connected: ["Finance", "Projects", "Administration", "Reports"],
    preview: [
      {
        title: "Pipeline Board",
        note: "Stage-based tracking to improve conversion.",
        chips: ["Stages", "Drag & Drop", "Insights"],
      },
      {
        title: "Deal Activity",
        note: "Never miss follow-ups and next steps.",
        chips: ["Tasks", "Reminders", "Notes"],
      },
    ],
  },
  projects: {
    icon: "ti ti-layout-kanban",
    title: "Projects",
    badge: "Delivery",
    description:
      "Deliver work with structured boards, tasks and ownership. Track progress and tie effort back to timesheets and reporting.",
    capabilities: [
      "Kanban boards and task workflow",
      "Assignees, priorities and progress tracking",
      "Collaboration-ready task views",
      "Timesheet and reporting alignment",
    ],
    reports: [
      "Project status",
      "Task completion trends",
      "Owner workload",
      "Delivery summaries",
    ],
    connected: ["Attendance", "HR", "CRM", "Reports"],
    preview: [
      {
        title: "Kanban Boards",
        note: "Simple flow from backlog to done.",
        chips: ["Backlog", "In Progress", "Done"],
      },
      {
        title: "Task Detail",
        note: "Owners, due dates, and clear accountability.",
        chips: ["Owners", "Deadlines", "Updates"],
      },
    ],
  },
  finance: {
    icon: "ti ti-calculator",
    title: "Finance",
    badge: "Accounting",
    description:
      "Manage invoicing and expenses with clearer reporting. Keep financial operations connected to CRM and delivery work for better visibility.",
    capabilities: [
      "Invoices and billing workflows",
      "Expenses tracking and summaries",
      "Reporting across revenue and costs",
      "Connected records from sales to cash",
    ],
    reports: [
      "Invoice summaries",
      "Expense summaries",
      "Revenue vs costs",
      "Finance exports",
    ],
    connected: ["CRM", "Projects", "Payroll", "Reports"],
    preview: [
      {
        title: "Invoices",
        note: "Create, track and export invoices cleanly.",
        chips: ["Create", "Track", "Export"],
      },
      {
        title: "Expenses",
        note: "Capture expenses for accurate reporting.",
        chips: ["Categories", "Receipts", "Summaries"],
      },
    ],
  },
};

const deepMeta = computed(
  () => DEEP_CONTENT[deepActive.value] || DEEP_CONTENT.hr
);

//** -------------------- Request demo  -------------------- **/

const submitting = ref(false);
const success = ref(false);
const error = ref("");
const touched = ref(false);

const demo = ref({
  full_name: "",
  company_name: "",
  email: "",
  preferred_contact: "email", // email | phone
  phone: "",
  company_size: "",
  preferred_date: "",
  preferred_time: "",
  timezone: "Africa/Nairobi",
  notes: "",
  consent: false,
});

let demoModalInstance = null;
let contactSalesModalInstance = null;

onMounted(() => {
  const el = document.getElementById("request_demo");
  if (el) demoModalInstance = new Modal(el, { backdrop: "static" });

  const salesEl = document.getElementById("contact_sales_modal");
  if (salesEl) {
    contactSalesModalInstance = new Modal(salesEl, { backdrop: "static" });
  }

  maybeOpenWorkspaceFromQuery();
});

watch(
  () => route.query.workspace,
  (value) => {
    if (value === "required") maybeOpenWorkspaceFromQuery();
  }
);

function openDemoModal() {
  resetDemo(false); // keep any typed values if you want? (here we keep by default)
  demoModalInstance?.show();
}

function openContactSalesModal(plan) {
  selectedSalesPlan.value = plan || null;
  contactSalesModalInstance?.show();
}

function closeDemoModal() {
  demoModalInstance?.hide();
}

function resetDemo(clear = true) {
  error.value = "";
  touched.value = false;
  success.value = false;

  if (clear) {
    demo.value = {
      full_name: "",
      company_name: "",
      email: "",
      preferred_contact: "email",
      phone: "",
      company_size: "",
      preferred_date: "",
      preferred_time: "",
      timezone: "Africa/Nairobi",
      notes: "",
      consent: false,
    };
  }
}

function isValidEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
}

function validateDemo() {
  const d = demo.value;

  if (!d.full_name) return "Full name is required.";
  if (!d.company_name) return "Company name is required.";
  if (!isValidEmail(d.email)) return "Enter a valid email.";
  if (!d.preferred_contact) return "Choose a preferred contact method.";
  if (d.preferred_contact === "phone" && !d.phone)
    return "Phone is required when contact is Phone.";
  if (!d.company_size) return "Select company size.";
  if (!d.preferred_date) return "Pick a preferred date.";
  if (!d.preferred_time) return "Pick a preferred time.";
  if (!d.timezone) return "Timezone is required.";
  if (!d.consent) return "Consent is required.";

  return "";
}

async function submitDemoRequest() {
  touched.value = true;
  error.value = "";

  const msg = validateDemo();
  if (msg) {
    error.value = msg;
    return;
  }

  submitting.value = true;
  try {
    await publicPlanService.requestDemo(demo.value);
    success.value = true;
    toast("Demo request sent!", "success");
  } catch (e) {
    error.value =
      e?.response?.data?.message ||
      "Failed to submit request. Please try again.";
    toast(error.value, "error");

  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped>
/* page bg */
.bg-page {
  background: radial-gradient(
      900px 380px at 15% 0%,
      rgba(59, 130, 246, 0.16),
      transparent 60%
    ),
    radial-gradient(
      900px 380px at 85% 10%,
      rgba(99, 102, 241, 0.14),
      transparent 60%
    ),
    #f8fafc;
}

/* navbar */
.nav-blur {
  backdrop-filter: blur(10px);
}

.navbar {
  --bs-navbar-padding-y: 0.75rem;
}

.navbar .navbar-brand small {
  max-width: 520px;
}

.navbar .btn {
  border-radius: 999px;
}

/* brand */
.brand-badge {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(59, 130, 246, 0.12);
  color: #1d4ed8;
  border: 1px solid rgba(59, 130, 246, 0.18);
}

/* hero */
.hero-wrap {
  background: linear-gradient(
    180deg,
    #ffffff 0%,
    rgba(255, 255, 255, 0.2) 100%
  );
}
.badge-pill {
  background: rgba(59, 130, 246, 0.08);
  border: 1px solid rgba(59, 130, 246, 0.16);
  color: #1d4ed8;
  padding: 10px 14px;
  border-radius: 999px;
}
.dot {
  width: 5px;
  height: 5px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.2);
  display: inline-block;
}

.logo-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  color: #334155;
  font-size: 0.875rem;
}

/* mockup top */
.mockup-top {
  padding: 12px 14px;
  background: rgba(15, 23, 42, 0.02);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.dot-red,
.dot-yellow,
.dot-green {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  display: inline-block;
}
.dot-red {
  background: rgba(239, 68, 68, 0.65);
}
.dot-yellow {
  background: rgba(245, 158, 11, 0.65);
}
.dot-green {
  background: rgba(34, 197, 94, 0.65);
}

/* product shell */
.product-shell {
  border-radius: 18px;
  background: #fff;
  overflow: hidden;
}

/* tabs */
.tabs-wrap {
  display: flex;
  gap: 8px;
  padding: 10px;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(2, 6, 23, 0.02);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.tabs-wrap::-webkit-scrollbar {
  display: none;
}

.tab-btn {
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  border-radius: 999px;
  padding: 8px 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: #334155;
  white-space: nowrap;
  transition: all 0.15s ease;
  flex: 0 0 auto;
}
.tab-btn:hover {
  transform: translateY(-1px);
}
.tab-btn.active {
  background: rgba(59, 130, 246, 0.1);
  border-color: rgba(59, 130, 246, 0.22);
  color: #1d4ed8;
}

/* screen */
.screen-wrap {
  border-radius: 18px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  overflow: hidden;
  background: radial-gradient(
      800px 240px at 20% 0%,
      rgba(59, 130, 246, 0.12),
      transparent 60%
    ),
    radial-gradient(
      800px 240px at 80% 20%,
      rgba(99, 102, 241, 0.1),
      transparent 60%
    ),
    #ffffff;
}
.screen-inner {
  padding: 14px;
}
.screen-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}
.screen-kpi {
  padding: 10px 12px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.screen-grid {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 12px;
}
.screen-card {
  border-radius: 18px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  padding: 14px;
}
.screen-card--lite {
  background: rgba(2, 6, 23, 0.02);
}

.chip-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  font-size: 0.875rem;
  color: #0f172a;
}
.chip-pill i {
  color: #16a34a;
}
.chip-pill--sm {
  padding: 6px 10px;
  font-size: 0.825rem;
}

.mini-list {
  display: grid;
  gap: 10px;
}
.mini-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.95rem;
  color: #334155;
}

/* stats */
.stats-card {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  padding: 16px;
  box-shadow: 0 10px 30px rgba(2, 6, 23, 0.04);
}
.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.16);
  color: #4338ca;
}

/* common */
.bg-soft-primary {
  background-color: rgba(59, 130, 246, 0.08);
}

/* feature cards */
.feature-card {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 10px 28px rgba(2, 6, 23, 0.05);
}
.feature-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.16);
  color: #1d4ed8;
}

/* strip */
.strip-card {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 10px 28px rgba(2, 6, 23, 0.05);
}
.strip-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.16);
  color: #16a34a;
}

/* steps */
.step-card {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 10px 28px rgba(2, 6, 23, 0.05);
}
.step-num {
  width: 44px;
  height: 44px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.16);
  color: #1d4ed8;
  font-weight: 800;
  margin-bottom: 10px;
}

/* testimonials */
.testimonial-card {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 10px 28px rgba(2, 6, 23, 0.05);
}
.avatar-pill {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
  color: #0f172a;
  font-weight: 800;
}

/* pricing */
.pricing-shell {
  border-radius: 22px;
}
.toggle-wrap {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(0, 0, 0, 0.06);
  flex-wrap: wrap;
  justify-content: center;
  text-align: center;
}
.plan-card {
  border-radius: 22px;
}
.plan-card--recommended {
  outline: 2px solid rgba(59, 130, 246, 0.35);
  box-shadow: 0 18px 45px rgba(59, 130, 246, 0.12);
}
.annual-license-price {
  min-height: 96px;
}
.annual-license-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(15, 23, 42, 0.08);
  color: #334155;
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.plan-features {
  background: rgba(2, 6, 23, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

/* compare table */
.compare-table-wrap {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  box-shadow: 0 10px 28px rgba(2, 6, 23, 0.05);
}
.compare-table thead th {
  background: rgba(2, 6, 23, 0.02);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.compare-table td,
.compare-table th {
  padding: 14px 12px;
  border-color: rgba(0, 0, 0, 0.06);
}
.compare-table tbody tr:hover td {
  background: rgba(59, 130, 246, 0.04);
}

/* CTA */
.cta-card {
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.12),
    rgba(99, 102, 241, 0.1)
  );
  border: 1px solid rgba(59, 130, 246, 0.18);
  border-radius: 22px;
  padding: 22px;
}

/* Deep Dive */
.deep-menu {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 10px 28px rgba(2, 6, 23, 0.05);
}
.deep-menu-head {
  padding: 16px;
  background: rgba(2, 6, 23, 0.02);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.deep-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 14px;
  border: 0;
  background: transparent;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  transition: background 0.15s ease;
}
.deep-item:hover {
  background: rgba(59, 130, 246, 0.04);
}
.deep-item.active {
  background: rgba(59, 130, 246, 0.08);
}
.deep-ico {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.16);
  color: #1d4ed8;
}
.deep-cta {
  padding: 16px;
}

.deep-panel {
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 10px 28px rgba(2, 6, 23, 0.05);
}
.deep-panel-top {
  padding: 18px;
  background: radial-gradient(
      900px 220px at 20% 0%,
      rgba(59, 130, 246, 0.14),
      transparent 60%
    ),
    rgba(2, 6, 23, 0.02);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.deep-panel-ico {
  width: 52px;
  height: 52px;
  border-radius: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.12);
  border: 1px solid rgba(59, 130, 246, 0.18);
  color: #1d4ed8;
  font-size: 1.2rem;
}

.deep-card {
  border-radius: 18px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  padding: 16px;
}

.deep-strip {
  border-radius: 18px;
  background: rgba(2, 6, 23, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.06);
  padding: 14px 16px;
}

.deep-preview {
  border-radius: 18px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  overflow: hidden;
}
.deep-preview-head {
  padding: 14px 16px;
  background: rgba(2, 6, 23, 0.02);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.deep-preview-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 14px;
}
.preview-tile {
  border-radius: 18px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: radial-gradient(
      800px 200px at 20% 0%,
      rgba(99, 102, 241, 0.08),
      transparent 60%
    ),
    #ffffff;
  padding: 14px;
}

/* Sticky behavior */
@media (min-width: 992px) {
  .sticky-lg-top {
    top: 90px;
  }
}

/* =========================
   WORLD-CLASS MOBILE LAYER
   ========================= */

/* Better mobile container breathing room (keeps Bootstrap structure) */
@media (max-width: 576px) {
  .container {
    padding-left: 16px;
    padding-right: 16px;
  }

  /* Navbar: make collapse feel premium */
  .navbar .navbar-brand {
    gap: 10px !important;
  }
  .navbar .navbar-brand small {
    display: none; /* cleaner on small screens */
  }
  .navbar .navbar-toggler {
    border-radius: 12px;
    padding: 8px 10px;
  }
  #publicNavbar {
    margin-top: 12px;
    padding: 12px;
    border-radius: 18px;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 12px 30px rgba(2, 6, 23, 0.08);
  }
  #publicNavbar .navbar-nav .nav-link {
    padding: 10px 12px;
    border-radius: 12px;
  }
  #publicNavbar .navbar-nav .nav-link:active {
    background: rgba(59, 130, 246, 0.08);
  }
  #publicNavbar .d-flex.gap-2 {
    margin-top: 12px;
  }
  #publicNavbar .btn {
    width: 100%;
    padding: 12px 14px;
    font-weight: 700;
  }

  /* HERO typography + spacing */
  .hero-wrap {
    padding-top: 28px !important;
    padding-bottom: 26px !important;
  }
  .badge-pill {
    width: 100%;
    justify-content: center;
    text-align: center;
    gap: 10px;
    padding: 10px 12px;
  }
  .display-5 {
    font-size: 1.9rem;
    line-height: 1.15;
    letter-spacing: -0.02em;
  }
  .lead {
    font-size: 1.02rem;
    line-height: 1.55;
  }

  /* Primary CTAs: stack, full-width, thumb-friendly */
  .hero-wrap .btn-lg {
    width: 100%;
    border-radius: 16px;
    padding: 14px 16px;
  }
  .hero-wrap .d-flex.flex-wrap.align-items-center.gap-2 {
    gap: 10px !important;
  }

  /* Social proof pills: nice scroll instead of wrapping messy */
  .hero-wrap .d-flex.flex-wrap.gap-2 {
    flex-wrap: nowrap !important;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 6px;
    scrollbar-width: none;
  }
  .hero-wrap .d-flex.flex-wrap.gap-2::-webkit-scrollbar {
    display: none;
  }
  .logo-pill {
    flex: 0 0 auto;
  }

  /* Product Preview card: less cramped, better hierarchy */
  .product-shell {
    border-radius: 20px;
  }
  .mockup-top {
    padding: 10px 12px;
  }
  .tabs-wrap {
    padding: 8px;
    border-radius: 14px;
  }
  .tab-btn {
    padding: 10px 12px;
    font-size: 0.92rem;
  }
  .screen-inner {
    padding: 12px;
  }
  .screen-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }
  .screen-kpi {
    border-radius: 16px;
    padding: 10px 10px;
  }
  .screen-grid {
    grid-template-columns: 1fr;
  }
  .mini-row {
    font-size: 0.95rem;
  }

  /* Sections: tighter but not cramped */
  section.py-5 {
    padding-top: 36px !important;
    padding-bottom: 36px !important;
  }

  /* Feature cards: slightly bigger padding for touch comfort */
  .feature-card,
  .step-card,
  .testimonial-card,
  .strip-card,
  .stats-card {
    border-radius: 20px;
    padding: 16px;
  }

  /* Deep dive: turn left menu into a horizontal “module rail” */
  .deep-menu {
    border-radius: 20px;
  }
  .deep-menu-head {
    padding: 14px 14px;
  }
  .deep-menu .deep-item {
    border-bottom: 0;
  }

  /* Makes deep items behave like chips rail on mobile */
  .deep-menu {
    padding-bottom: 8px;
  }
  .deep-menu button.deep-item {
    display: inline-flex;
    width: auto;
    min-width: 240px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 18px;
    margin: 10px 10px 0 0;
    padding: 12px 12px;
    background: #fff;
    box-shadow: 0 8px 20px rgba(2, 6, 23, 0.05);
  }
  .deep-menu {
    overflow-x: auto;
    white-space: nowrap;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  .deep-menu::-webkit-scrollbar {
    display: none;
  }
  .deep-cta {
    display: none; /* keep focus on content on small screens */
  }

  /* Deep panel spacing */
  .deep-panel {
    border-radius: 20px;
  }
  .deep-panel-top {
    padding: 14px;
  }
  .deep-panel-ico {
    width: 46px;
    height: 46px;
    border-radius: 16px;
  }
  .deep-preview-grid {
    grid-template-columns: 1fr;
    padding: 12px;
  }

  /* Pricing: make plan cards feel premium */
  .pricing-shell .card-body {
    padding: 18px !important;
  }
  .toggle-wrap {
    width: 100%;
    border-radius: 18px;
    padding: 10px 12px;
    gap: 10px;
  }
  .plan-card {
    border-radius: 22px;
  }
  .plan-card .btn {
    border-radius: 16px;
    padding: 12px 14px;
    font-weight: 800;
  }

  /* Compare table: mobile “card-like” horizontal snap experience */
  .compare-table-wrap {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scroll-snap-type: x mandatory;
  }
  .compare-table {
    min-width: 820px; /* forces horizontal scroll for readability */
  }
  .compare-table thead th {
    position: sticky;
    top: 0;
    z-index: 1;
  }

  /* FAQ: larger tap targets */
  .accordion-button {
    padding: 16px 14px;
    border-radius: 0 !important;
  }
  .accordion-body {
    line-height: 1.6;
  }

  /* Footer */
  footer .container {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 6px !important;
  }
}

/* Small tablets */
@media (min-width: 577px) and (max-width: 991.98px) {
  .screen-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .screen-grid {
    grid-template-columns: 1fr;
  }
  .deep-preview-grid {
    grid-template-columns: 1fr;
  }
}

.tabs-wrap {
  scroll-snap-type: x proximity;
}
.tab-btn {
  scroll-snap-align: start;
}

/* =========================
   REQUEST DEMO MODAL (DQ)
   ========================= */
.dq-demo .modal-dialog {
  max-width: 860px;
}
.dq-demo-card {
  border: 0;
  border-radius: 22px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 30px 70px rgba(2, 6, 23, 0.18);
}
.dq-demo-header {
  padding: 18px 18px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: radial-gradient(
      900px 220px at 20% 0%,
      rgba(59, 130, 246, 0.14),
      transparent 60%
    ),
    rgba(2, 6, 23, 0.02);
}
.dq-demo-icon {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.12);
  border: 1px solid rgba(59, 130, 246, 0.18);
  color: #1d4ed8;
  font-size: 1.15rem;
  flex: 0 0 auto;
}
.dq-demo-subtitle {
  color: #64748b;
  font-size: 0.95rem;
}
.dq-sales-card {
  overflow: hidden;
}
.dq-sales-icon {
  background: rgba(16, 185, 129, 0.12);
  border-color: rgba(16, 185, 129, 0.22);
  color: #047857;
}
.dq-sales-hero {
  padding: 18px;
  border-radius: 20px;
  background:
    linear-gradient(
      135deg,
      rgba(16, 185, 129, 0.12),
      rgba(59, 130, 246, 0.08)
    ),
    #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-sales-eyebrow {
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #0f766e;
}
.dq-sales-contact {
  height: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(255, 255, 255, 0.96);
  color: #0f172a;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.dq-sales-contact:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
}
.dq-sales-contact-icon {
  width: 48px;
  height: 48px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.18);
  color: #1d4ed8;
  font-size: 1.1rem;
  flex: 0 0 auto;
}
.dq-sales-contact-label {
  display: block;
  margin-bottom: 2px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
}
.dq-close {
  border-radius: 12px;
}

.dq-demo-body {
  padding: 18px;
}
.dq-demo-footer {
  padding: 16px 18px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(2, 6, 23, 0.02);
  gap: 4px;
}
.dq-input {
  border-radius: 14px !important;
  border-color: rgba(0, 0, 0, 0.1);
  padding: 12px 12px;
}
.dq-input:focus {
  box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.15);
  border-color: rgba(59, 130, 246, 0.35);
}
.dq-mini-note {
  border-radius: 16px;
  padding: 12px 12px;
  background: rgba(59, 130, 246, 0.06);
  border: 1px solid rgba(59, 130, 246, 0.14);
  color: #334155;
  display: flex;
  gap: 10px;
  align-items: center;
}
.dq-mini-note i {
  color: #1d4ed8;
}

/* segmented control */
.dq-seg {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  padding: 8px;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(2, 6, 23, 0.02);
}
.dq-seg-btn {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 999px;
  padding: 10px 12px;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 800;
  color: #334155;
  transition: all 0.15s ease;
}
.dq-seg-btn:hover {
  transform: translateY(-1px);
}
.dq-seg-btn.active {
  background: rgba(59, 130, 246, 0.12);
  border-color: rgba(59, 130, 246, 0.22);
  color: #1d4ed8;
}

/* modules chips */
.dq-chip-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px;
  border-radius: 18px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(2, 6, 23, 0.02);
}
.dq-chip {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 999px;
  padding: 9px 12px;
  background: #fff;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  color: #334155;
  transition: all 0.15s ease;
}
.dq-chip i {
  opacity: 0.5;
}
.dq-chip.active {
  background: rgba(34, 197, 94, 0.1);
  border-color: rgba(34, 197, 94, 0.25);
  color: #166534;
}
.dq-chip.active i {
  opacity: 1;
  color: #16a34a;
}
.dq-invalid-box {
  outline: 2px solid rgba(239, 68, 68, 0.25);
}

/* success state */
.dq-success {
  text-align: center;
  padding: 18px 10px;
}
.dq-success-badge {
  width: 64px;
  height: 64px;
  margin: 0 auto 10px;
  border-radius: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(34, 197, 94, 0.12);
  border: 1px solid rgba(34, 197, 94, 0.18);
  color: #16a34a;
  font-size: 1.6rem;
}

/* mobile */
@media (max-width: 576px) {
  .dq-demo .modal-dialog {
    margin: 10px;
  }
  .dq-demo-body {
    padding: 14px;
  }
  .dq-demo-footer {
    flex-direction: column;
    gap: 10px;
  }
  .dq-demo-footer .btn {
    width: 100%;
    border-radius: 16px;
    padding: 12px 14px;
    font-weight: 800;
  }
}
</style>


